import { useEffect, useState, useRef, useCallback, useContext } from "react"
import { useGlobalEnvConfig } from "@environment/GlobalEnvConfigProvider";
import { Trans } from '@lingui/macro';
import mapboxgl from 'mapbox-gl';
import InteractiveMap from '@components/interactive-map';
import SlideUp from '@components/slide-up'
import { safeImageUrl, getLetters, getTextColorClass, getColor, getRouteColor, getIcon, createCurvedRoute, getLightenedColor, getLayerStyles, getLayerData, getMarkerStyle, routeMarkerStyle } from '@utils/Utils';
import { useIsMobile } from '@utils/hooks/useIsMobile';
import { TinyColor } from "@ctrl/tinycolor";
import { useDispatch, useSelector } from 'react-redux';
import { setMapMarkers } from '@utils/redux/slices/map';
import { BasicData } from '@App';

import './style.scss';

const InteractiveMapWrapper = ({ basicData, activeMarkers = [] }) => {
    const { REACT_APP_RESOURCE_DOMAIN } = useGlobalEnvConfig();
    const { theme } = useContext(BasicData);

    let markerIndex = 0;
    const dispatch = useDispatch();

    const colors = getColor(theme);
    const activeTextColor = getTextColorClass(colors?.secondary);
    const activePinTextColor = getTextColorClass(colors?.accent);
    const mobile = useIsMobile();

    const [interactiveMap, setInteractiveMap] = useState(null);
    const [activeMarkerCollection, setActiveMarkerCollection] = useState();
    const [markerState, setMarkerState] = useState(null);
    const [layerState, setLayerState] = useState(null);
    const [mapConfig, setMapConfig] = useState({});
    const [timeLineConfig, setTimelineConfig] = useState(null)
    const [activeOverlappingPins, setactiveOverlappingPins] = useState({});
    const markersStore = useSelector(state => state.map.mapMarkers);

    const prevZoom = useRef(0);
    const initialBounds = useRef();
    const reCenterBounds = useRef();
    const timelineRefs = useRef();
    const mapMarkerRefs = useRef();
    const layerData = useRef([]);
    const markerData = useRef([]);
    const overlappingPinsRefs = useRef([]);
    const overlappingPinsData = useRef();
    const prevGroup = useRef({});


    //INTERACTIVE MAP FUNCTIONS AND EVENTS
    const handleOverlappingPinActiveState = useCallback((marker) => {
        let overlappingPinsIndex = null;
        let overlappingPinMarkerIndex = null

        overlappingPinsData?.current?.forEach((group, gi) => {
            group.forEach((p, gmi) => {
                if (p.markerIndex === marker.markerIndex) {
                    overlappingPinsIndex = gi;
                    overlappingPinMarkerIndex = gmi;
                }
            })
        })

        setactiveOverlappingPins({
            markerData: marker,
            overlappingPinsIndex: overlappingPinsIndex,
            overlappingPinMarkerIndex: overlappingPinMarkerIndex,
            markerGroup: overlappingPinsRefs.current[overlappingPinsIndex]
        })
    }, [])


    //Creates a route layer including the marker in the middle
    const createRoute = useCallback((leg, route) => {
        let layerGeometry = [];
        let markerCoordinates = [];
        let bearing = 0;
        let children = {};
        let accommodation = {};
        let thumbnail = '';
        let name = '';
        let rotateIcon = route.mode === "ScheduledFlight" || (route.mode === "CharterFlight" && (route.variant === "charterFlight_Plane" || route.variant === ""));
        const accommodations = basicData?.accommodations;

        if (leg.type === "Mobile" && (route.mode === "BoatNoRoute" || route.mode === "Boat" || route.mode === "TrainNoRoute" || route.mode === "Train" || route.mode === "Transfer")) {
            accommodation = accommodations[leg.accommodation_id];
            thumbnail = accommodation?.images ? safeImageUrl(REACT_APP_RESOURCE_DOMAIN, accommodation?.images[0], 380, 250, "c") : '';
            name = accommodations[leg.accommodation_id]?.name;
        }

        if (route.points && route.points.length > 2) {
            layerGeometry = route?.points;
            markerCoordinates = route?.points[Math.floor(route?.points.length / 2)];
        } else {
            const curve = createCurvedRoute(route);
            layerGeometry = curve?.points;
            markerCoordinates = curve?.midPoint;
            bearing = curve?.bearing;
        }

        if (route.mode === "ScheduledFlight" || route.mode === "CharterFlight" || route.mode === "BoatNoRoute" || route.mode === "TrainNoRoute" || route.mode === "Train" || route.mode === "Boat") {
            children = {
                mapChildren:
                    <div data-zoomlevel="0" className={`icon route-marker ${route.mode.toLowerCase()}`} style={routeMarkerStyle(route)}>
                        <i style={rotateIcon ? { transform: `rotate(${bearing.toFixed()}deg)` } : {}} className={`${getIcon(route.mode, route.variant)}`}></i>
                    </div>,
                timelineChildren: <>
                    <div className={`icon route-marker ${route.mode.toLowerCase()}`} style={routeMarkerStyle(route)}>
                        <i style={rotateIcon ? { transform: `rotate(${bearing.toFixed()}deg)` } : {}} className={`${getIcon(route.mode, route.variant)}`}></i>
                    </div>
                    <div className='details'>

                        {thumbnail &&
                            <img alt="" src={thumbnail} />
                        }

                        <div>
                            {name && <p>{name}</p>}
                            {(route.from && route.mode !== 'Train') && <p>{route.from}</p>}
                            {(route.route_label) && <p className='xs'>{route.route_label ? <span>{route.route_label}</span> : ''}</p>}
                            {route.duration && <p className='xs'>{route.duration}</p>}
                        </div>

                        {(route.start_time || route.end_time) &&
                            <div>
                                {route.start_time && <p className='xs'><Trans>Depart</Trans>: <span>{route.start_time}</span></p>}
                                {route.end_time && <p className='xs'><Trans>Arrive</Trans>: <span>{route.end_time}</span></p>}
                            </div>
                        }
                    </div>
                </>
            }
        } else if (route.mode === "NoTransit" || route.mode === "Placeholder") {
            children = {
                mapChildren: <div className={`icon route-marker ${route.mode.toLowerCase()}`}></div>,
                timelineChildren: <div className={`icon route-marker ${route.mode.toLowerCase()}`}></div>
            }
        } else {
            children = {
                mapChildren:
                    <div data-zoomlevel={route.distance > 100000 ? 0 : 8} className={`icon route-marker ${route.mode.toLowerCase()}`} style={routeMarkerStyle(route)}>
                        <i className={`${getIcon(route.mode, route.variant)}`}></i>
                    </div>,
                timelineChildren: <>
                    <div className={`icon route-marker ${route.mode.toLowerCase()}`} style={routeMarkerStyle(route)}>
                        <i className={`${getIcon(route.mode, route.variant)}`}></i>
                    </div>
                    <div className='details'>
                        <div><p>{route.route_label}</p></div>

                        {(route.distance || route.duration) &&
                            <div>
                                {route.distance && route.distance > 0 && <p className='xs'>{`${route.distance} km`}</p>}
                                {route.duration && <p className='xs'>{route.duration}</p>}
                            </div>
                        }
                    </div>
                </>
            }
        }


        if (markerCoordinates.length && layerGeometry.length) {
            markerData.current = [...markerData.current, {
                id: `route-marker-${markerIndex}`,
                latitude: markerCoordinates[1] || 0,
                longitude: markerCoordinates[0] || 0,
                name: route.route_label,
                elementClass: route?.mode ? route?.mode?.toLowerCase() : 'pin-marker',
                mode: route.mode,
                type: 'route',
                leg_id: leg.id,
                children: children
            }]

            layerData.current = [...layerData.current, {
                layerStyles: getLayerStyles(markerIndex, getRouteColor(route.mode)),
                geoJson: getLayerData(layerGeometry),
                mode: route.mode
            }]

            markerIndex++;
        }
    }, [basicData?.accommodations, markerIndex, REACT_APP_RESOURCE_DOMAIN])


    const setMutedMapLayers = useCallback((layerCopy) => {
        if (layerCopy) {
            layerCopy.forEach((route, i) => {
                if (route.layerStyles && route.layerStyles?.length) {
                    const color = getLightenedColor(route.mode);
                    route.layerStyles[0].paint = { 'line-color': color, 'line-width': 5 };
                    route.layerStyles[1].paint = { 'circle-color': color, 'circle-radius': 6 };
                }
            })
            setLayerState(layerCopy)
        }
    }, [])


    const setMutedMapMarkers = useCallback(() => {
        const legIdToModeMap = {};

        markerState.forEach(marker => {
            const { mode, leg_id } = marker;
            if (leg_id && !legIdToModeMap[leg_id]) {
                legIdToModeMap[leg_id] = mode;
            }
        });

        mapMarkerRefs?.current?.forEach((marker, i) => {
            const currentMode = markerState[i].mode;
            const currentLegId = markerState[i].leg_id;
            let modeToUse = currentMode;

            if (!modeToUse && currentLegId) {
                modeToUse = legIdToModeMap[currentLegId];
            }
            
            const color = modeToUse ? getLightenedColor(modeToUse) : new TinyColor(`#${colors?.accent}`).tint(60).toHexString();

            if (!currentMode && marker?.current?.children[0]?.classList.contains('standard-accommodation')) {
                marker?.current?.children[0]?.style.setProperty("background-color", new TinyColor(`#${colors?.accent}`).tint(60).toHexString()); 
            } else if (marker?.current) {
                marker?.current?.children[0]?.style.setProperty("background-color", color);
            }
        });

        timelineRefs?.current?.forEach((marker, i) => {
            const currentMode = markerState[i].mode;
            const currentLegId = markerState[i].leg_id;
            let modeToUse = currentMode;

            if (!modeToUse && currentLegId) {
                modeToUse = legIdToModeMap[currentLegId];
            }
            
            const color = modeToUse ? getLightenedColor(modeToUse) : new TinyColor(`#${colors?.accent}`).tint(60).toHexString();
            if (!currentMode && marker?.current.classList.contains('standard-accommodation')) {
                marker?.current?.children[0].children[0].style.setProperty("background-color", new TinyColor(`#${colors?.accent}`).tint(60).toHexString()); 
            } else if (marker?.current){
                marker?.current?.children[0].children[0].style.setProperty("background-color", color);
            }
        })

    }, [colors?.accent, markerState])


    const onMarkerClick = useCallback((markerRef, index, prevIndex, timelineRefs, map, name) => {
        const element = timelineRefs.current[index].current;
        const root = markerRef.closest('.interactive-map');
        const layerCopy = JSON.parse(JSON.stringify(layerState));

        if (overlappingPinsRefs?.current) {
            handleOverlappingPinActiveState(markerState[index])
        }

        if (element) {
            timelineRefs.current.forEach((el) => { el.current.classList.remove('active', activeTextColor) })
            timelineRefs.current[index].current.classList.add('active', activeTextColor);
            if (name === 'Own Arrangement') {return}
            let time = 0;

            if (!root.querySelector('.map-timeline-overlay').classList.contains('open')) {
                setTimelineConfig(prev => ({
                    ...prev,
                    timeLineOptions: {
                        ...prev?.timeLineOptions,
                        isOpen: true,
                    },
                }))

                //Let the open animation finish first before scrolling
                time = 400;
            }

            const t = setTimeout(() => {
                element.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
                clearTimeout(t)
            }, time);
        }

        if (markerRef) {
            const line = layerCopy[index]?.geoJson?.features?.filter(x => x.geometry.type === "LineString");
            const lngLat = markerRef._lngLat ?? { lng: markerState[index].longitude, lat: markerState[index].latitude };

            let padding = mobile ? 30 : root.querySelector('.map-timeline-overlay').classList.contains('open') ? { left: (window.innerWidth * 0.2), top: 100, right: 100, bottom: 100 } : 100;

            const observer = new IntersectionObserver((entries, observer) => {
                if (!entries[0].isIntersecting) {

                    if (line) {
                        const coordinates = line[0].geometry.coordinates ? line[0].geometry.coordinates : lngLat;
                        const bounds = new mapboxgl.LngLatBounds(
                            coordinates[0],
                            coordinates[0]
                        );

                        for (const coord of coordinates) {
                            bounds.extend(coord);
                        }

                        map.current.fitBounds(bounds, {
                            padding: padding
                        });
                    } else {
                        map.current.flyTo({
                            center: [lngLat.lng, lngLat.lat],
                            essential: true,
                            zoom: 7
                        });
                    }
                } else {
                    observer.disconnect()
                }
            }, {
                root: root,
                threshold: [1],
                rootMargin: mapConfig?.timeLineOptions?.isOpen || !mobile ? mapConfig?.timeLineOptions?.alignment === "left" ? `100px 100px 100px -320px` : "100px -320px 100px 100px" : "100px",
            });

            observer.observe(markerRef);

            const siblings = root.querySelectorAll('.icon');
            siblings.forEach((el) => { el.classList.remove('active') });

            //Ignore overlapping pins
            if (markerRef.classList.contains('marker-content')) {
                markerRef.children[0]?.classList.add('active');
            }
        }

        //Mute the rest of the map
        setMutedMapMarkers();
        setMutedMapLayers(layerCopy);

        //Set active color on clicked route and marker
        if (layerCopy[index] && layerCopy[index].layerStyles) {
            const color = getRouteColor(layerCopy[index].mode);
            layerCopy[index].layerStyles[0].paint = { 'line-color': color, 'line-width': 5 };
            layerCopy[index].layerStyles[1].paint = { 'circle-color': color, 'circle-radius': 6 };
        }

        if (markerState[index]?.type === "route") {
            map?.current?.moveLayer(`line-route-${index}`);
            map?.current?.moveLayer(`line-point-layer-${index}`);
        }

        setActiveMarkerCollection([index]);

    }, [markerState, activeTextColor, mobile, mapConfig?.timeLineOptions, handleOverlappingPinActiveState, setMutedMapMarkers, setMutedMapLayers, layerState]);


    //MAPBOX EVENTS
    const onZoom = useCallback((event) => {
        let mapZoom = event?.viewState?.zoom;
        let zoomLevel = Math.trunc(mapZoom);

        if (zoomLevel !== prevZoom.current) {
            const rm = document.querySelectorAll('.route-marker');
            for (let i = 0, len = rm.length; i < len; i++) {
                parseInt(rm[i].dataset?.zoomlevel) <= zoomLevel ? rm[i].classList.add('show') : rm[i].classList.remove('show');
            }

            prevZoom.current = zoomLevel;
        }
    }, [])

    const onMapLoad = useCallback((map, tRefs, mRefs, opRefs, opData) => {
        if (map && map?.current) {
            setInteractiveMap(map);
        }

        if (opRefs.current && opData) {
            overlappingPinsRefs.current = opRefs.current;
            overlappingPinsData.current = opData;
        }

        if (tRefs.current) {
            timelineRefs.current = tRefs.current;
            timelineRefs?.current[0].current?.classList?.add('active', activeTextColor);
        }

        if (mRefs.current) {
            mapMarkerRefs.current = mRefs.current;

            if (mapMarkerRefs.current[0].current?.classList.contains('marker-content')) {
                mapMarkerRefs.current[0].current?.children[0]?.classList.add('active');
            } else {
                const overlappingPinsParent = mapMarkerRefs.current[0].current?.closest('.overlapping-pins')
    
                if (overlappingPinsParent) {
                    overlappingPinsParent.querySelector('.primary-marker').classList.add('active');
                }
            }
        }
    }, [activeTextColor])


    const onRecenter = useCallback(() => {
        const root = interactiveMap?.current.getMap()._container.closest('.interactive-map');

        if (!Array.isArray(reCenterBounds.current)) {
            interactiveMap?.current?.fitBounds(reCenterBounds.current, {
                padding: mobile ? 30 : root.querySelector('.map-timeline-overlay').classList.contains('open') ? { left: (window.innerWidth * 0.2), top: 100, right: 100, bottom: 100 } : 100
            });
        } else {
            interactiveMap?.current?.flyTo({
                center: reCenterBounds.current,
                essential: true,
                zoom: 7
            });
        }
    }, [interactiveMap, mobile])


    const onTimelineOpen = useCallback((isOpen) => {
        if (!isOpen && activeMarkers.length) {
            const element = timelineRefs.current[activeMarkers[0]].current;

            if (element) {
                const t = setTimeout(() => {
                    element.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
                    clearTimeout(t)
                }, 400);
            }
        }
    }, [activeMarkers])


    //USEEFFECTS
    
    // change colors on map based on DTC changes
    useEffect(() => {
        if (!timelineRefs?.current || !mapMarkerRefs?.current) return;

        const updatePinColor = (pins, color) => {
            const pinsList = Array.isArray(pins) ? pins : [pins];

            pinsList.forEach((pin) => {
                if (pin) {
                    pin.classList.remove('dark-text', 'light-text');
                    pin.classList.add(color);

                    const pinMarker = pin.closest('.pin-marker');
                    if (pinMarker) {
                        pinMarker.style.setProperty("background-color", new TinyColor(colors?.accent).toHexString());
                    }
                }
            });
        };

        const getValidPinsTimelineAndMap = (refs) => {
            return refs
                .map(ref => ref?.current)
                .filter(ref => {
                    const pin = ref?.querySelector('.pin-marker p');
                    return pin && 
                           !ref.classList.contains('boat') && 
                           !ref.classList.contains('train') && 
                           !ref.classList.contains('transfer') && 
                           (pin.classList.contains('light-text') || pin.classList.contains('dark-text'));
                })
                .map(ref => ref.querySelector('.pin-marker p'));
        };
    
        const activeTimelineCard = timelineRefs.current.find((timelineRef) =>
            timelineRef.current.classList.contains('active')
        );
    
        if (activeTimelineCard?.current) {
            activeTimelineCard.current.classList.remove('dark-text', 'light-text');
            activeTimelineCard.current.classList.add(activeTextColor);
        }

        const timelinePins = getValidPinsTimelineAndMap(timelineRefs.current);
        updatePinColor(timelinePins, activePinTextColor);

        const mapPins = getValidPinsTimelineAndMap(mapMarkerRefs.current);
        updatePinColor(mapPins, activePinTextColor);

        overlappingPinsRefs.current.forEach(element => updatePinColor(element.current.querySelector('.primary-marker p'), activePinTextColor))
    }, [timelineRefs, mapMarkerRefs, activePinTextColor, activeTextColor, colors?.accent]);   

    useEffect(() => {
        setMapConfig({
            overlappingPinsOptions: {
                pinStyles: {
                    primaryPin: {
                        color: activePinTextColor
                    },
                    secondaryPin: {},
                    secondaryPinWrapper: {
                        backgroundColor: new TinyColor(`#${colors?.accent}`).setAlpha(0.3)
                    }
                }
            },
            mapStyles: { position: 'relative', width: '100%', height: '100%' },
            maxZoom: 14,
            showControls: true,
            mapFont: basicData?.theme?.headings_font,
            initialViewState: {
                fitBoundsOptions: {
                    padding: mobile ? 30 : { left: (!mobile ? window.innerWidth * 0.2 : 100), top: 100, right: 100, bottom: 100 }
                }
            }
        })
    }, [activePinTextColor, basicData?.theme?.headings_font, colors?.accent, mobile])


    useEffect(() => {
        setTimelineConfig({
            showTimeline: true,
            timeLineOptions: {
                alignment: "left",
                buttonLabel: 'Show Route Info',
                showToggle: !mobile,
                showRecenter: true,
                isOpen: !activeMarkers.length,
                timelineStyles: {
                    backgroundColor: new TinyColor(`#${colors?.secondary}`).setAlpha(0.3)
                }
            }
        })
    }, [colors?.secondary, mobile, activeMarkers?.length])


    useEffect(() => {
        if (activeOverlappingPins) {
            const groupRef = activeOverlappingPins.markerGroup?.current;
            const color = new TinyColor(`#${colors?.accent}`).tint(60).toHexString();

            if (groupRef) {
                groupRef.firstChild.querySelector('.pin-marker').querySelector('p').textContent = activeOverlappingPins.markerData?.letter;
                groupRef.firstChild.querySelector('.primary-marker').classList.add('active');
                const isSameGroup = activeOverlappingPins.overlappingPinsIndex === prevGroup.current?.overlappingPinsIndex;

                if (!isSameGroup && prevGroup.current) {
                    const prevMarker = prevGroup?.current?.markerGroup?.current;

                    if (prevMarker) {
                        prevMarker.firstChild.querySelector('.primary-marker').classList.remove('active');
                    }
                }

                if (activeOverlappingPins.overlappingPinsIndex > prevGroup.current.overlappingPinsIndex && isSameGroup) {
                    groupRef.firstChild.lastChild.style.transform = `translateX(${activeOverlappingPins.overlappingPinMarkerIndex * 40}px)`

                } else {
                    groupRef.firstChild.lastChild.style.transform = `translateX(-${activeOverlappingPins.overlappingPinMarkerIndex * 40}px)`
                }
            }

            if (activeOverlappingPins?.overlappingPinsIndex !== null) {
                const activeGroup = overlappingPinsRefs.current[activeOverlappingPins?.overlappingPinsIndex];

                if (activeGroup) {
                    const secondaryMarkers = activeGroup.current.querySelector('.secondary-markers').querySelectorAll('.icon');
                    secondaryMarkers.forEach((el) => {
                        el.style.setProperty("background-color", color);
                    })

                    overlappingPinsRefs.current.forEach((group, i) => {
                        if (i !== activeOverlappingPins?.overlappingPinsIndex) {
                            const inactiveMarkers = group.current.querySelectorAll('.icon');

                            inactiveMarkers.forEach((el) => {
                                el.style.setProperty("background-color", color);
                            })
                        }
                    })
                } else {
                    overlappingPinsRefs.current.forEach((group, i) => {
                        const inactiveMarkers = group.current.querySelectorAll('.icon');

                        inactiveMarkers.forEach((el) => {
                            el.style.setProperty("background-color", color);
                        })
                    })
                }
            } else {
                overlappingPinsRefs.current.forEach((group, i) => {
                    const inactiveMarkers = group.current.querySelectorAll('.icon');
                    group.current.querySelector('.primary-marker').classList.remove('active');

                    inactiveMarkers.forEach((el) => {
                        el.style.setProperty("background-color", color);
                    })
                })
            }

            prevGroup.current = activeOverlappingPins
        }
    }, [activeOverlappingPins, colors?.accent])


    //On map tab click the activeMarkers are changed
    useEffect(() => {
        if (interactiveMap && activeMarkers.length) {
            if (timelineRefs?.current && mapMarkerRefs?.current) {
                const marker = markersStore?.filter(x => x.index === activeMarkers[0]);
                const activeTimelineElement = timelineRefs?.current[activeMarkers[0]];
                const activeRouteElement = mapMarkerRefs?.current[activeMarkers[0]];

                if (marker) {
                    const leg = basicData?.trip_summary?.legs.filter(x => x.id === marker[0]?.leg_id);
                    const accommodations = basicData?.accommodations;

                    if (overlappingPinsRefs?.current) {
                        handleOverlappingPinActiveState(markerState[activeMarkers[0]]);
                    }

                    if (leg) {
                        const accommodation = accommodations[leg[0].accommodation_id];

                        if (accommodation) {
                            const coords = [[accommodation.longitude], [accommodation.latitude]]
                            reCenterBounds.current = coords;
                            interactiveMap?.current?.flyTo({
                                center: coords,
                                essential: true,
                                zoom: 7
                            });
                        }

                        setTimelineConfig(prev => ({
                            ...prev,
                            timeLineOptions: {
                                ...prev?.timeLineOptions,
                                isOpen: false,
                            },
                        }))
                    }
                }

                setActiveMarkerCollection(activeMarkers)

                if (activeTimelineElement && activeRouteElement) {
                    timelineRefs.current.map(x => x.current?.classList?.remove('active', activeTextColor));
                    activeTimelineElement.current.classList.add('active', activeTextColor);
                    mapMarkerRefs.current?.map(x => x.current?.children[0]?.classList?.remove('active'));

                    if (activeRouteElement.current?.classList.contains('marker-content')) {
                        activeRouteElement.current?.children[0].classList.add('active');
                    }
                }
            }
        }
    }, [activeMarkers, interactiveMap, activeTextColor, basicData?.accommodations, basicData?.trip_summary?.legs, handleOverlappingPinActiveState, markersStore, markerState])


    useEffect(() => {
        if (basicData && layerData?.current?.length === 0 && markerData?.current?.length === 0) {
            const legs = basicData?.trip_summary?.legs;
            const routes = basicData?.trip_summary?.routes;
            const accommodations = basicData?.accommodations;
            const destinations = basicData?.destinations;

            let letterIndex = 0;

            legs?.forEach((leg, i) => {
                let legRoutes = [];
                let endLegRoutes = [];
                let initialRoutes = routes?.filter(x => x.start_leg_id === 0 && x.end_leg_id === leg.id); // routes before first accommodation;
                initialRoutes?.forEach((route) => { createRoute(leg, route) });

                //Accommodation markers are created
                if (leg?.accommodation_id) {
                    const accommodation = accommodations[leg.accommodation_id];
                    const tumbnail = accommodation?.images ? safeImageUrl(REACT_APP_RESOURCE_DOMAIN, accommodation?.images[0], 380, 250, "c") : '';
                    const letter = getLetters(letterIndex)

                    //Igonore mobile legs if they have accommodations - We only want the routes
                    if (leg.type !== "Mobile") {
                        const routeFallback = routes?.find(x => x.start_leg_id !== 0 && x.end_leg_id !== 0 && (x.start_leg_id === leg.id || x.end_leg_id === leg.id));
                      
                        markerData.current = [...markerData?.current, {
                            id: `pin-marker-${markerIndex}`,
                            latitude: accommodation?.latitude || routeFallback?.end_content_entity_location[0] || 0,
                            longitude: accommodation?.longitude || routeFallback?.end_content_entity_location[1] || 0,
                            name: accommodation?.name,
                            elementClass: `pin-marker pin-marker-${markerIndex} standard-accommodation`,
                            type: 'pin',
                            accommodation_id: leg.accommodation_id,
                            element_id: `destination-${leg.id}-${leg.destination_id}`,
                            leg_id: leg.id,
                            markerIndex: markerIndex,
                            letterIndex: letterIndex,
                            letter: letter,
                            children: {
                                mapChildren:
                                    <div className={`icon pin-marker pin-marker-${markerIndex} standard-accommodation`} style={getMarkerStyle()}>
                                        <span><p className={activePinTextColor}>{letter}</p></span>
                                    </div>,
                                timelineChildren: <>
                                    <div className={`icon pin-marker`} style={getMarkerStyle()}>
                                        <span><p className={activePinTextColor}>{letter}</p></span>
                                    </div>
                                    <div className="details">
                                        {tumbnail &&
                                            <img alt="" src={tumbnail} />
                                        }

                                        <div>
                                            <p>{accommodations[leg.accommodation_id]?.name}</p>

                                            {(accommodation.category || leg.nights) &&
                                                <p className='xs'>{accommodation.category ? <span>{accommodation.category} &bull;</span> : ''} {leg.nights && <span>{leg.nights} {leg.nights === 1 ? <Trans>Night</Trans> : <Trans>Nights</Trans>}</span>}</p>
                                            }
                                        </div>

                                    </div>
                                </>
                            }
                        }]

                        //Add dummy null values to maintain indexing
                        layerData.current = [...layerData.current, {
                            geoJson: null,
                            layerStyles: null,
                        }]

                        letterIndex++;
                        markerIndex++;

                        legRoutes = routes?.filter(x => x.start_leg_id !== 0 && x.end_leg_id !== 0 && x.start_leg_id === leg.id);
                        legRoutes?.forEach((route) => {createRoute(leg, route); });

                    } else if (leg.type === "Mobile") {

                        if (leg.stops) {

                            leg.stops?.forEach((legStop,index) => {
                                const letter = getLetters(letterIndex)
                                const route = routes?.find(route => route.start_leg_id === leg.id && route.start_day === legStop.end_day);
                                const destination = destinations[legStop.destination_id];
                                const mobileRouteFirstInstance = routes?.find(route => route.start_leg_id === leg.id);
        
                                if (accommodation.category !== 'Train') {
                                    legRoutes = routes?.filter(route => route.start_leg_id !== 0 && route.end_leg_id !== 0 && route.start_leg_id === leg.id && route.start_leg_id === route.end_leg_id && route.to === legStop.name);
                                    legRoutes?.forEach((route) => {createRoute(leg, route); });
                                }

                                    if(index === 0) {
                                        if (accommodation.category !== 'Mobile Safari') {
                                            legRoutes = routes?.filter(route => route.start_leg_id !== 0 && route.end_leg_id !== 0 && route.start_leg_id === leg.id && route.start_leg_id === route.end_leg_id);
                                            legRoutes?.forEach((route) => {createRoute(leg, route); });
                                        }
                                     
                                        if (accommodation.category !== 'Train' && accommodation.category !== 'Cruise Ship') {
                                            markerData.current = [...markerData?.current, {
                                                id: `pin-marker-${markerIndex}`,
                                                latitude: destination.latitude || 0,
                                                longitude: destination.longitude || 0,
                                                name: accommodation?.name,
                                                elementClass: `pin-marker pin-marker-${markerIndex} ${mobileRouteFirstInstance?.mode?.toLowerCase()} disabled`,
                                                type: 'pin',
                                                accommodation_id: leg.accommodation_id,
                                                element_id: `destination-${leg.id}-${legStop.destination_id}`,
                                                leg_id: leg.id,
                                                markerIndex: markerIndex,
                                                letterIndex: letterIndex,
                                                letter: letter,
                                                children: {
                                                    mapChildren:
                                                        <div className={`icon pin-marker pin-marker-${markerIndex} ${mobileRouteFirstInstance?.mode?.toLowerCase()}`} style={{...getMarkerStyle(colors.accent, mobileRouteFirstInstance), display: 'none'}}>
                                                            <span><p className={activePinTextColor}>{letter}</p></span>
                                                        </div>,
                                                    timelineChildren: <>
                                                        <div className={`icon route-marker ${route?.mode?.toLowerCase()}`} style={routeMarkerStyle(route)}>
                                                            
                                                        </div>
                                                        <div className='details'>
                                                            {tumbnail &&
                                                                <img alt="" src={tumbnail} />
                                                            }

                                                            <div>
                                                                {accommodation.name && <p>{accommodation.name}</p>}
                                                                {(accommodation.category || leg.nights) && <p className='xs'>{accommodation.category ? <span>{accommodation.category} &bull;</span> : ''} {leg.nights && <span>{leg.nights} {leg.nights === 1 ? <Trans>Night</Trans> : <Trans>Nights</Trans>}</span>}</p>}

                                                            </div>
                                                        </div>
                                                    </>
                                                }
                                            }]

                                            layerData.current = [...layerData.current, {
                                                geoJson: null,
                                                layerStyles: null,
                                            }]

                                            markerIndex++;
                                        }
                                    }

                                    markerData.current = [...markerData?.current, {
                                        id: `pin-marker-${markerIndex}`,
                                        latitude: route?.start_content_entity_location[0] || destination.latitude || 0,
                                        longitude: route?.start_content_entity_location[1] || destination.longitude || 0,
                                        name: accommodation?.name,
                                        elementClass: `pin-marker pin-marker-${markerIndex} ${mobileRouteFirstInstance?.mode?.toLowerCase()}`,
                                        type: 'pin',
                                        accommodation_id: leg.accommodation_id,
                                        element_id: `destination-${leg.id}-${legStop.destination_id}`,
                                        leg_id: leg.id,
                                        markerIndex: markerIndex,
                                        letterIndex: letterIndex,
                                        letter: letter,
                                        children: {
                                            mapChildren:
                                                <div className={`icon pin-marker pin-marker-${markerIndex} ${mobileRouteFirstInstance?.mode?.toLowerCase()}`}style={getMarkerStyle(colors.accent, mobileRouteFirstInstance)}>
                                                    <span><p className={activePinTextColor}>{letter}</p></span>
                                                </div>,
                                            timelineChildren: <>
                                                <div className={`icon pin-marker`} style={getMarkerStyle(colors.accent, mobileRouteFirstInstance, true)}>
                                                    <span><p className={activePinTextColor}>{letter}</p></span>
                                                </div>
                                                <div className="details">        
                                                    <div>
                                                        <p>{legStop.type}</p>
                                                        {(legStop.name || destinations[legStop.destination_id]?.name) && (
                                                            <p className='xs'>{legStop.name || destinations[legStop.destination_id]?.name}</p>
                                                        )}
                                                    </div>
                                                </div>
                                            </>
                                        }
                                    }]
            
                                     //Add dummy null values to maintain indexing
                                     layerData.current = [...layerData.current, {
                                        geoJson: null,
                                        layerStyles: null,
                                    }]
            
                                    letterIndex++;
                                    markerIndex++; 

                                if (index === leg.stops.length-1) {
                                    legRoutes = routes?.filter(route => route.start_leg_id !== 0 && route.end_leg_id !== 0 && route.start_leg_id === leg.id && route.start_leg_id !== route.end_leg_id);
                                    legRoutes?.forEach((route) => {createRoute(leg, route); });
                                }
                            });
                        } else {
                            legRoutes = routes?.filter(route => route.start_leg_id !== 0 && route.end_leg_id !== 0 && route.start_leg_id === leg.id && (route.start_leg_id === route.end_leg_id || route.start_leg_id !== route.end_leg_id));
                            legRoutes?.forEach((route) => {createRoute(leg, route); });
                        }
                    }
                    
                } else if (!leg.accommodation_id && leg.id !== 0) { 
                    // OwnArrangement pins and overnightTravel
                    const accommodation = '';
                    const tumbnail = '';
                    const letter = getLetters(letterIndex);

                    if (leg.type === 'OvernightTravel') {
                        markerData.current = [...markerData?.current, {
                            id: `pin-marker-${markerIndex}`,
                            latitude: i * 10 / 100, // fixes overlapping pins issue
                            longitude: i * 10 / 100, // fixes overlapping pins issue
                            name: leg.name,
                            elementClass: `pin-marker pin-marker-${markerIndex} disabled`,
                            type: 'pin',
                            accommodation_id: null,
                            element_id: `destination-${leg.id}`,
                            leg_id: leg.id,
                            markerIndex: markerIndex,
                            letterIndex: letterIndex,
                            letter: letter,
                            children: {
                                mapChildren:
                                    <>
                                       
                                    </>,
                                timelineChildren: <>
                                    <div className={`icon pin-marker`} style={getMarkerStyle()}>
                                        <span><p className={activePinTextColor}>{letter}</p></span>
                                    </div>
                                    <div className="details">
                                        {tumbnail &&
                                            <img alt="" src={tumbnail} />
                                        }
                                        <div>
                                            <p>{leg.name}</p>
    
                                            {(accommodation.category || leg.nights) &&
                                                <p className='xs'>{accommodation.category ? <span>{accommodation.category} &bull;</span> : ''} {leg.nights && <span>{leg.nights} {leg.nights === 1 ? <Trans>Night</Trans> : <Trans>Nights</Trans>}</span>}</p>
                                            }
                                        </div>
                                    </div>
                                </>
                            }
                        }];
                        
                        //Add dummy null values to maintain indexing
                        layerData.current = [...layerData.current, {
                            geoJson: null,
                            layerStyles: null,
                        }]
    
                        // letterIndex++;
                        markerIndex++;
                    } else {
                        markerData.current = [...markerData?.current, {
                            id: `pin-marker-${markerIndex}`,
                            latitude: i * 10 / 100, // fixes overlapping pins issue
                            longitude: i * 10 / 100, // fixes overlapping pins issue
                            name: leg.name,
                            elementClass: `pin-marker pin-marker-${markerIndex}`,
                            type: 'pin',
                            accommodation_id: null,
                            element_id: `destination-${leg.id}`,
                            leg_id: leg.id,
                            markerIndex: markerIndex,
                            letterIndex: letterIndex,
                            letter: letter,
                            children: {
                                mapChildren:
                                    <>
                                       
                                    </>,
                                timelineChildren: <>
                                    <div className={`icon pin-marker`} style={getMarkerStyle()}>
                                        <span><p className={activePinTextColor}>{letter}</p></span>
                                    </div>
                                    <div className="details">
                                        {tumbnail &&
                                            <img alt="" src={tumbnail} />
                                        }
                                        <div>
                                            <p>{leg.name}</p>
    
                                            {(accommodation.category || leg.nights) &&
                                                <p className='xs'>{accommodation.category ? <span>{accommodation.category} &bull;</span> : ''} {leg.nights && <span>{leg.nights} {leg.nights === 1 ? <Trans>Night</Trans> : <Trans>Nights</Trans>}</span>}</p>
                                            }
                                        </div>
                                    </div>
                                </>
                            }
                        }];
                        
                        //Add dummy null values to maintain indexing
                        layerData.current = [...layerData.current, {
                            geoJson: null,
                            layerStyles: null,
                        }]
    
                        letterIndex++;
                        markerIndex++;
                    }

                    legRoutes = routes?.filter(route =>  route.start_leg_id !== 0 && route.end_leg_id !== 0 && route.start_leg_id === leg.id);
                    legRoutes?.forEach((route) => {createRoute(leg, route); });
                }
                
                endLegRoutes = routes?.filter(route => route.end_leg_id === 0 && route.start_leg_id === leg.id);
                endLegRoutes?.forEach((route) => { createRoute(leg, route) });
            })

            if (markerData.current?.length > 0) {
                setMarkerState(markerData.current);
            }

            if (layerData.current?.length > 0) {
                setLayerState(layerData.current)
            }
        }
    }, [basicData, activePinTextColor, createRoute, markerIndex, dispatch, colors.accent, REACT_APP_RESOURCE_DOMAIN])


    //Update redux for access to marker indexes etc outside of the map
    useEffect(() => {
        if (!markersStore?.length) {
            dispatch(setMapMarkers(markerState?.map((m, i) => {
                return { id: m.element_id, index: i, leg_id: m.leg_id }
            })))
        }
    }, [markerState, dispatch, markersStore])


    useEffect(() => {
        if (basicData && layerState?.length) {
            let boundCoords = layerState.filter(r => r.geoJson).map((route) => {
                return route?.geoJson?.features.map((feature) => {
                    return feature?.geometry?.coordinates;
                })
            }).flat(2);

            if (!boundCoords?.length) {
                boundCoords = markerState.map((marker) => {
                    return [marker.longitude, marker.latitude]
                });
            }

            const initalBounds = new mapboxgl.LngLatBounds(
                boundCoords[0],
                boundCoords[0]
            );

            for (const coord of boundCoords) {
                initalBounds.extend(coord);
            };

            if (initalBounds) {
                initialBounds.current = initalBounds;
                reCenterBounds.current = initalBounds;

                setMapConfig(prev => ({
                    ...prev,
                    initialViewState: {
                        bounds: initalBounds,
                        fitBoundsOptions: {
                            padding: mobile ? 30 : { left: (!mobile ? window.innerWidth * 0.2 : 100), top: 100, right: 100, bottom: 100 }
                        }
                    }
                }))
            }
        }
    }, [layerState, basicData, mobile, markerState])

    interactiveMap?.current?.on('resize', () => {
        interactiveMap?.current?.fitBounds(initialBounds.current, {
            padding: 30
        });
    })

    const onSlideUpOpen = useCallback((isOpen) => {
        if (isOpen) {
            //Fixes bug where map is rendered in hidden container so gets default sizes
            setTimeout(() => {
                interactiveMap?.current?.resize();
            }, 0);
        }
    }, [interactiveMap])

    if (!Object.keys(basicData?.accommodations ?? {}).length && !Object.keys(basicData?.trip_summary?.routes ?? {}).length) return;

    return (
        <>
            {markerState && layerState && mapConfig &&
                <div className={`map-wrapper bg-white`}>
                    {mobile
                        ? <SlideUp header="map" onOpen={onSlideUpOpen}>
                            <div className="mobile-map-placeholder"><div className="map-placeholder-btn"><h6><Trans>View on the Map</Trans></h6></div></div>
                            <InteractiveMap
                                activeMarkers={activeMarkerCollection}
                                onMapLoad={onMapLoad}
                                layerData={layerState}
                                markerData={markerState}
                                config={mapConfig}
                                timelineConfig={timeLineConfig}
                                onMarkerClick={onMarkerClick}
                                onZoom={onZoom}
                                onRecenter={onRecenter}
                                onTimelineOpen={onTimelineOpen}
                            />
                        </SlideUp>
                        : <InteractiveMap
                            activeMarkers={activeMarkerCollection}
                            onMapLoad={onMapLoad}
                            layerData={layerState}
                            markerData={markerState}
                            config={mapConfig}
                            timelineConfig={timeLineConfig}
                            onMarkerClick={onMarkerClick}
                            onZoom={onZoom}
                            onRecenter={onRecenter}
                            onTimelineOpen={onTimelineOpen}
                        />
                    }

                </div>
            }
        </>
    )
}

export default InteractiveMapWrapper